import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from './api';

export type ResultadoLote<T> = { ok: T[]; fallidos: Array<{ item: T; motivo: string }> };

/**
 * Corre una operación sobre varios elementos con pocas en paralelo: de a una
 * sería lento con cientos, y todas juntas saturarían la API y el storage. Un
 * fallo no corta el resto; se informa al final cuáles no se pudieron.
 */
export async function enLote<T>(
  items: T[],
  fn: (item: T) => Promise<unknown>,
  concurrencia = 4,
): Promise<ResultadoLote<T>> {
  const ok: T[] = [];
  const fallidos: Array<{ item: T; motivo: string }> = [];
  let siguiente = 0;

  async function trabajador() {
    while (siguiente < items.length) {
      const item = items[siguiente++];
      try {
        await fn(item);
        ok.push(item);
      } catch (err) {
        fallidos.push({ item, motivo: err instanceof ApiError ? err.message : 'Error inesperado.' });
      }
    }
  }

  await Promise.all(Array.from({ length: Math.min(concurrencia, items.length) }, trabajador));
  return { ok, fallidos };
}

/**
 * Resume un lote para mostrarlo: mensaje recibe "12" o "11 de 12" y arma la
 * frase; los fallidos se nombran (los tres primeros) con su motivo.
 */
export function resumenLote<T>(
  r: ResultadoLote<T>,
  total: number,
  nombre: (item: T) => string,
  mensaje: (cantidad: string) => string,
): { aviso: string | null; error: string | null } {
  const cantidad = r.ok.length === total ? String(total) : `${r.ok.length} de ${total}`;
  const aviso = r.ok.length > 0 ? mensaje(cantidad) : null;
  if (r.fallidos.length === 0) return { aviso, error: null };

  const detalle = r.fallidos
    .slice(0, 3)
    .map((f) => `${nombre(f.item)} (${f.motivo})`)
    .join('; ');
  const mas = r.fallidos.length > 3 ? ` y ${r.fallidos.length - 3} más` : '';
  return { aviso, error: `No se pudo con ${r.fallidos.length}: ${detalle}${mas}.` };
}

/**
 * Selección sobre una lista de claves. Shift+clic selecciona el rango desde el
 * último marcado. Lo que deja de estar en la lista (se borró, se movió) sale
 * solo de la selección.
 */
export function useSeleccion<K>(claves: K[]) {
  const [seleccion, setSeleccion] = useState<Set<K>>(() => new Set());
  const ancla = useRef<K | null>(null);

  useEffect(() => {
    setSeleccion((previa) => {
      if (previa.size === 0) return previa;
      const vigentes = new Set(claves);
      const filtrada = new Set([...previa].filter((k) => vigentes.has(k)));
      return filtrada.size === previa.size ? previa : filtrada;
    });
  }, [claves]);

  const alternar = useCallback(
    (clave: K, conShift: boolean) => {
      // El ancla se lee antes de actualizarla: el updater de setSeleccion corre
      // más tarde, cuando ancla.current ya apunta a este mismo elemento.
      const desde = ancla.current === null ? -1 : claves.indexOf(ancla.current);
      const hasta = claves.indexOf(clave);
      ancla.current = clave;

      setSeleccion((previa) => {
        const nueva = new Set(previa);
        const marcar = !previa.has(clave);

        if (conShift && desde !== -1 && hasta !== -1) {
          const [a, b] = desde < hasta ? [desde, hasta] : [hasta, desde];
          for (const k of claves.slice(a, b + 1)) {
            if (marcar) nueva.add(k);
            else nueva.delete(k);
          }
        } else if (marcar) {
          nueva.add(clave);
        } else {
          nueva.delete(clave);
        }
        return nueva;
      });
    },
    [claves],
  );

  const estado = useMemo<'ninguno' | 'algunos' | 'todos'>(() => {
    if (seleccion.size === 0) return 'ninguno';
    return claves.every((k) => seleccion.has(k)) ? 'todos' : 'algunos';
  }, [seleccion, claves]);

  const alternarTodos = useCallback(() => {
    setSeleccion(estado === 'todos' ? new Set() : new Set(claves));
  }, [estado, claves]);

  const limpiar = useCallback(() => {
    setSeleccion(new Set());
    ancla.current = null;
  }, []);

  return { seleccion, alternar, alternarTodos, limpiar, estado };
}

/** Casilla de "todos", con el estado intermedio cuando hay algunos marcados. */
export function CasillaTodos({
  estado,
  onChange,
  etiqueta,
}: {
  estado: 'ninguno' | 'algunos' | 'todos';
  onChange: () => void;
  etiqueta: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = estado === 'algunos';
  }, [estado]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="casilla"
      checked={estado === 'todos'}
      onChange={onChange}
      aria-label={etiqueta}
    />
  );
}

export function Casilla({
  marcada,
  onCambio,
  etiqueta,
}: {
  marcada: boolean;
  onCambio: (conShift: boolean) => void;
  etiqueta: string;
}) {
  return (
    <input
      type="checkbox"
      className="casilla"
      checked={marcada}
      aria-label={etiqueta}
      // onClick y no onChange: es el único que trae shiftKey.
      onClick={(e) => onCambio(e.shiftKey)}
      onChange={() => undefined}
    />
  );
}

/** Aparece arriba de la lista mientras haya algo seleccionado. */
export function BarraSeleccion({
  cantidad,
  onLimpiar,
  nota,
  children,
}: {
  cantidad: number;
  onLimpiar: () => void;
  /** Aclaración opcional, p. ej. que hay elementos sin cargar todavía. */
  nota?: string | null;
  children: React.ReactNode;
}) {
  if (cantidad === 0) return null;
  return (
    <div className="barra-seleccion" role="toolbar" aria-label="Acciones sobre la selección">
      <span className="cantidad">
        {cantidad} {cantidad === 1 ? 'seleccionado' : 'seleccionados'}
        {nota && <span className="nota"> · {nota}</span>}
      </span>
      <div className="acciones">{children}</div>
      <button className="link" onClick={onLimpiar}>
        Quitar selección
      </button>
    </div>
  );
}
